import type { ReactNode } from 'react';
import { styled } from 'styled-components';

import { Button } from '#/components/Button/Button';
import { InfoBox } from '#/components/InfoBox/InfoBox';
import { useI18n } from '#/i18n/hooks/useI18n';
import { Modal } from '#/modals/Modal';
import { spacing } from '#/theme/theme';
import { hideModal } from '#/zustand-stores/modalStore';

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: ${spacing[24]};
`;

type Props = {
  type: 'danger' | 'warning' | 'info';
  title?: string;
  buttonText: string;
  body?: ReactNode;
  submitting: boolean;
  extraButtons?: ReactNode;
  qaId?: string;
  confirm: () => Promise<void> | void;
  cancel?: () => void;
};

export function ConfirmationModal({
  type,
  title,
  buttonText,
  body,
  submitting,
  extraButtons,
  qaId,
  confirm,
  cancel,
}: Props) {
  const { i18n } = useI18n();

  const onCancel = () => {
    hideModal();
    if (cancel) {
      cancel();
    }
  };

  return (
    <Modal title={title} onClose={onCancel} qaId={qaId}>
      {body && (
        <InfoBox
          $type={type}
          $boxStyle="modal"
          $marginTop={2}
          $marginBottom={4}
        >
          {body}
        </InfoBox>
      )}

      <ButtonContainer>
        <Button
          type="button"
          variant={type === 'danger' ? 'danger' : 'primary'}
          onClick={confirm}
          disabled={submitting}
          qaId="confirm-button"
        >
          {buttonText}
        </Button>

        {extraButtons}
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          qaId="cancel-button"
        >
          {i18n('Cancel')}
        </Button>
      </ButtonContainer>
    </Modal>
  );
}
